<script>
    import "../app.postcss";
    import { getStores, updated } from "$app/stores";
    import { beforeNavigate, goto } from "$app/navigation";
    import { onMount } from "svelte";
    import { GoogleAnalytics, ga } from "../lib/google-analytics";
    import { mobile } from "../stores/mobile.js";
    import { cart } from "../stores/cart";
    import { urlParamsToMap } from "../utils/url-params-to-map.js";
    import Header from "../components/header/Header.svelte";
    import Footer from "../components/footer/Footer.svelte";
    import Chatra from "../components/Chatra.svelte";
    import MobileHeader from "../components/mobile/header/MobileHeader.svelte";
    import Sidebar from "../lib/menu/MobileSidebar.svelte";
    import StructuredSearch from "../components/structured-data/StructuredSearch.svelte";
    import StructuredCompany from "../components/structured-data/StructuredCompany.svelte";
    import { ADMIN_ROLE, hasRole } from "../utils/roles.utils";
    import { cConsentStatus, cConsentStore } from "../components/c-banner/c-consent.store";
    import CookieBanner from "../components/c-banner/CBanner.svelte";
    import { cTypes } from "../components/c-banner/cs";
    import {
        PUBLIC_BING_ADS_ID,
        PUBLIC_CHATRA_ENABLED,
        PUBLIC_GADS_ID,
        PUBLIC_GA_ID,
        PUBLIC_META_ADS_ID,
    } from "$env/static/public";
    import { isTruthy } from "$utils/boolean.utils";
    import BingAds from "$lib/bing-ads/BingAds.svelte";
    import { bing } from "$lib/bing-ads";
    import MetaAds from "$lib/meta-ads/MetaAds.svelte";
    import { metaAds } from "$lib/meta-ads/functions";
    import { trStore } from "$lib/tr/tr.store";
    import { browser } from "$app/environment";

    /** @type {import('./$types').LayoutData} */
    export let data;

    let isMobile = data.session["mobile"];
    mobile.set(isMobile);

    let mobileSidebarOpen = false;

    /** @type {string[]} */
    const analyticsProps = [PUBLIC_GA_ID, PUBLIC_GADS_ID];
    let isAdminUser = hasRole(data.session["roles"], [ADMIN_ROLE]);
    const enableChatra = isTruthy(PUBLIC_CHATRA_ENABLED);
    const { page } = getStores();
    $: isAdminPages = $page.url.pathname.startsWith("/admin/");
    $: isCookiePage = $page.url.pathname.startsWith("/cookie-policy");

    beforeNavigate(({ willUnload, to }) => {
        if (browser && $updated && !willUnload && to?.url) {
            location.href = to.url.href;
        }
    });

    onMount(() => {
        checkTokenInUrl(document.location.href);
        return page.subscribe(({ url: { pathname } }) => {
            ga.addEvent("page_view", { page_path: pathname });
            bing.addEvent("page_view", { page_path: pathname });
            trStore.add({ event: "PageView", sourceUrl: pathname });
            metaAds.pageView();
        });
    });

    /** @param {string}  str */
    async function checkTokenInUrl(str) {
        const params = urlParamsToMap(str);
        if (!params.has("cart-token")) {
            return;
        }
        try {
            const res = await fetch(`/server-api/cart/import?cart-token=${params.get("cart-token")}`);
            if (res.status === 200) {
                const payload = await res.json();
                if (payload.cart) {
                    cart.loadExternalCart(payload.cart);
                    goto("/kassa");
                }
            }
        } catch (e) {
            console.log("Check token in URL failed: ", e.message);
        }
    }
</script>

<svelte:head>
    <StructuredCompany />
    <StructuredSearch />
    {#if enableChatra}
        <Chatra id="Ti3eGtfBLeRDdmR4f" />
    {/if}
    <MetaAds
        metaAdsPixelId={PUBLIC_META_ADS_ID}
        cookieConsent={$cConsentStore.types[cTypes.PERFORMANCE.id] === cConsentStatus.ACCEPTED}
    />
</svelte:head>

<GoogleAnalytics
    cookieConsent={$cConsentStore.types[cTypes.PERFORMANCE.id] === cConsentStatus.ACCEPTED}
    properties={analyticsProps}
    configurations={{
        [analyticsProps[0]]: { send_page_view: false },
        [analyticsProps[1]]: { allow_enhanced_conversions: true },
    }}
/>
<BingAds
    properties={[PUBLIC_BING_ADS_ID]}
    cookieConsent={$cConsentStore.types[cTypes.PERFORMANCE.id] === cConsentStatus.ACCEPTED}
/>

<div class="outer" data-sveltekit-reload={$updated ? "" : "off"}>
    <div class="main-inner" style="font-family: 'Avenir next', Avenir, 'Helvetica neue, Helvetica, Arial, sans-serif;">
        {#if !isMobile}
            <Header menuType={isAdminPages && isAdminUser ? "admin" : "user"} sales={data.sales} />
            <slot />
        {:else}
            <Sidebar menuType={isAdminPages && isAdminUser ? "admin" : "user"} bind:open={mobileSidebarOpen} />
            <MobileHeader bind:sidebar={mobileSidebarOpen} sales={data.sales} />
            <slot />
        {/if}
    </div>
    <div class="main-footer">
        <Footer />
    </div>
</div>
{#if !isCookiePage}
    <CookieBanner />
{/if}

<style>
    .outer {
        position: relative;
        display: flex;
        flex-direction: column;
        min-height: 1024px;
    }
    .main-inner {
        flex: 1 0 auto;
    }
    .main-footer {
        flex-shrink: 0;
    }
</style>
